import { useEffect, useState } from 'react';
import gettext from '@sdk/lang/gettext';
import useSlingApi from '@sdk/api/useSlingApi';
import useToast from '@sdk/common/toast/useToast';
import type { OrgSettings } from '@sdk/authentication/types/OrganisationType';
import type { LaborCostSettings, SaveLaborCostSettings } from '@sdk/labor/types';
import type { PersonasDict } from '@sdk/personaCollection/types/PersonasDict';

import type { UpdateOrgSettings } from '../onboardingChecklist.types';
import laborSettingsFormatter from '../../../settings/components/laborCost/helpers/laborSettingsFormatter';
import { ONBOARDING_TOAST_ID } from '../onboardingChecklist.consts';

import useOrgSettingsForm from './hooks/useOrgSettingsForm';
import { useLaborCostForm } from './hooks/useLaborCostForm';

type Props = {
  orgSettings: OrgSettings;
  saveLaborCostSettingsInAngular: SaveLaborCostSettings;
  personasDict: PersonasDict;
  close: () => void;
  updateOrgSettings: UpdateOrgSettings;
  isVisible: boolean;
};

export const useSetScheduleModal = ({
  orgSettings,
  saveLaborCostSettingsInAngular,
  close,
  personasDict,
  updateOrgSettings,
  isVisible,
}: Props) => {
  const { showSuccessToast } = useToast();
  const api = useSlingApi();
  const [laborCost, setLaborCost] = useState<LaborCostSettings>(null);
  const [isLoaded, setIsLoaded] = useState(true);

  useEffect(() => {
    (async (): Promise<void> => {
      if (isVisible) {
        try {
          const { data } = await api.laborCost.getLaborSettings();
          setLaborCost(laborSettingsFormatter(data));
          setIsLoaded(false);
        } catch (e) {
          console.error(e);
        }
      }
    })();
  }, [api.laborCost, isVisible]);

  const {
    fields: laborCostFields,
    submit: laborCostSubmit,
    dirty: laborCostDirty,
    reset: laborCostReset,
    submitErrors: laborCostErrors,
  } = useLaborCostForm({
    laborCost,
    saveLaborCostSettingsInAngular,
  });

  const {
    fields: orgSettingsFields,
    submit: orgSettingsSubmit,
    dirty: orgSettingsDirty,
    reset: orgSettingsReset,
    submitErrors: orgSettingsErrors,
  } = useOrgSettingsForm({ orgSettings, personasDict, updateOrgSettings });

  const onSubmitHandle = async (): Promise<void> => {
    try {
      if (laborCostDirty) await laborCostSubmit();
      if (orgSettingsDirty) await orgSettingsSubmit();

      showSuccessToast(gettext('Schedule rules have been set'), {
        containerId: ONBOARDING_TOAST_ID,
      });
      close();
    } catch (e) {
      return Promise.reject(e);
    }
  };

  return {
    laborCostFields,
    laborCostSubmit,
    laborCostReset,
    orgSettingsFields,
    orgSettingsSubmit,
    orgSettingsReset,
    onSubmitHandle,
    isLoaded,
    submitErrors: [...orgSettingsErrors, ...laborCostErrors],
  };
};
